import * as React from "react";
import { Tag } from "antd";
import { TableColumn } from "../../newcomponents/table/types";
import { GlobalJobExecution } from "./types";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { DateString } from "../../components/dateString";
import { formatStrings } from "../../utils/time";

export function getJobExecutionsColumns(orgName: string, timezoneLabel: string): TableColumn<GlobalJobExecution>[] {
  return [
    {
      id: "workloadName",
      label: "Workload",
      size: 180,
      enableResize: true,
      enableSort: true,
      cell: ({ row }) => {
        const exec = row.original;
        const { gvcName, workloadName, workloadLink } = exec;
        const path = `/console/org/${orgName}/gvc/${gvcName}/workload/${workloadName}`;
        return <InternalLink label={workloadName} to={path} />;
      },
    },
    {
      id: "gvcName",
      label: "GVC",
      size: 120,
      enableResize: true,
      enableSort: true,
      enableFilter: true,
      columnFilterType: "multi",
      cell: ({ row }) => {
        const exec = row.original;
        const path = `/console/org/${orgName}/gvc/${exec.gvcName}`;
        return <InternalLink label={exec.gvcName} to={path} />;
      },
    },
    {
      id: "name",
      label: "Execution",
      size: 200,
      enableResize: true,
      enableSort: true,
      cell: ({ row, getValue }) => {
        const exec = row.original;
        const name = getValue() as string;
        if (exec.commandId) {
          const path = `/console/org/${orgName}/gvc/${exec.gvcName}/workload/${exec.workloadName}/commands/${exec.commandId}`;
          return <InternalLink label={name || "-"} to={path} />;
        }
        return <span>{name || "-"}</span>;
      },
    },
    {
      id: "displayName",
      label: "Command Name",
      size: 180,
      enableResize: true,
      enableSort: true,
      cell: ({ row, getValue }) => {
        const exec = row.original;
        if (exec.triggerType !== "on-demand") {
          return <span style={{ color: "var(--color-label)" }}>-</span>;
        }
        const displayName = getValue() as string;
        // Only show if it's different from the raw name (i.e., has tags.name)
        if (displayName && displayName !== exec.name) {
          return <span className="font-medium">{displayName}</span>;
        }
        return <span style={{ color: "var(--color-label)" }}>-</span>;
      },
    },
    {
      id: "triggerType",
      label: "Source",
      size: 120,
      enableSort: true,
      enableFilter: true,
      columnFilterType: "single",
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value === "on-demand" ? <Tag color="blue">On-Demand</Tag> : <Tag>Cron Schedule</Tag>;
      },
    },
    {
      id: "location",
      label: "Location",
      size: 130,
      enableResize: true,
      enableSort: true,
      enableFilter: true,
      columnFilterType: "multi",
    },
    {
      id: "status",
      label: "Status",
      size: 110,
      enableSort: true,
      enableFilter: true,
      columnFilterType: "multi",
      cell: ({ getValue }) => {
        const status = getValue() as string;
        const colorMap: Record<string, string> = {
          active: "blue",
          successful: "success",
          failed: "error",
          pending: "warning",
          invalid: "error",
          removed: "orange",
        };
        const labelMap: Record<string, string> = {
          active: "Active",
          successful: "Successful",
          failed: "Failed",
          pending: "Pending",
          invalid: "Invalid",
          removed: "Removed",
        };
        return <Tag color={colorMap[status] || "default"}>{labelMap[status] || status}</Tag>;
      },
    },
    {
      id: "startTime",
      label: `Start Time (${timezoneLabel})`,
      size: 200,
      enableResize: true,
      enableSort: true,
      sortingFn: "cplnDate",
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value ? <DateString iso={value} format={formatStrings.sec} /> : "-";
      },
    },
    {
      id: "completionTime",
      label: `Completion Time (${timezoneLabel})`,
      size: 220,
      enableResize: true,
      enableSort: true,
      sortingFn: "cplnDate",
      cell: ({ getValue }) => {
        const value = getValue() as string;
        return value ? <DateString iso={value} format={formatStrings.sec} /> : "-";
      },
    },
  ];
}
